import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  createContext,
  ReactNode,
  use,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { HotkeysProvider } from "react-hotkeys-hook";
import {
  FloatingPanelContainer,
  useFloatingPanelStack,
} from "components/common/FloatingPanel";
import {
  EventLabellingModelStatus,
  ReviewPeriodData,
  ReviewSlotRevision,
} from "src/store/api/generatedApi";
import { useTenantTranslation } from "hooks/formatters";
import { useDisableHotkeyScopes } from "components/scrapEyeLabelling/hooks/useDisableHotkeyScopes";
import { ScrapEyesEventWithReviewData } from "./contexts/ManualReviewEventContext";
import {
  CapturedEvents,
  FetchedNeighbourPlayer,
} from "./FetchedNeighbourPlayer";
import { LABELLING_HOTKEY_SCOPES } from "./hotkeyScopes";
import { BringInDecision, BringInProgress } from "./utils/editScope";
import { formatUtcTimeRange } from "./utils/formatters";
import { LabellerExclusionDecisions } from "./utils/timelineExclusions";

dayjs.extend(utc);

export interface BringInPeriodRequest {
  targetPeriod: ReviewPeriodData;
  focusStart: Dayjs;
  focusEnd: Dayjs;
  // The page's viewing window: periods outside it are chain candidates, periods
  // inside it are verified in place.
  windowStart: Dayjs;
  windowEnd: Dayjs;
  reviewPeriods: ReviewPeriodData[];
  // The target period lies outside the loaded window, so its events must be
  // fetched into a modal-local context rather than read from the page.
  outOfWindow: boolean;
  progress: BringInProgress;
  // Current and original events from periods already brought in this chain. This hop's
  // focus overlaps them, so they seed it with their edits rather than the re-fetched
  // originals; absent for the first hop, which has none.
  carriedEvents?: ScrapEyesEventWithReviewData[];
  carriedOriginalEvents?: ScrapEyesEventWithReviewData[];
  // The periods this save will verify, so the modal's period bars show which of the
  // periods in view (target, in-scope seeds, accepted neighbours) confirming will verify.
  willVerifyPeriodIds?: readonly number[];
}

export interface BringInPeriodResult {
  reviewSlotRevisions?: ReviewSlotRevision[];
  decision: BringInDecision;
  // Every event across the whole focus window (target ± flank) — every period located
  // within it, including the target itself, derives its own slice from this one
  // consistently-paired set.
  focusEvents?: ScrapEyesEventWithReviewData[];
  // The focus window's DB originals and its seed (fetched overlaid with carried edits),
  // for the same per-period derivation: originals for the save, and whether a period was
  // edited from the diff against this seed.
  focusOriginalEvents?: ScrapEyesEventWithReviewData[];
  focusSeedEventsById?: Map<string, ScrapEyesEventWithReviewData>;
}

type BringInFn = (
  request: BringInPeriodRequest
) => Promise<BringInPeriodResult>;

const DIALOG_MARGIN = 3;

const LinkedPeriodBringInContext = createContext<BringInFn | null>(null);

const formatRange = (period: ReviewPeriodData): string =>
  formatUtcTimeRange(dayjs.utc(period.start), dayjs.utc(period.end));

export const LinkedPeriodBringInProvider = ({
  location,
  labellerExclusions,
  omittedModelStatuses,
  getHighlightColor,
  children,
}: {
  location: string;
  // What the page keeps out of its merge, per labeller. The modal maps these onto its own
  // window's labelling periods rather than resolving defaults for a window the page never saw.
  labellerExclusions: LabellerExclusionDecisions;
  // The statuses of the models the page leaves out of its response. The page has no decision
  // for those models, so the modal must leave them out the same way or it would merge them.
  omittedModelStatuses: readonly EventLabellingModelStatus[];
  // Set only by a caller with its own per-event outline rule (Targeted Review, colouring by
  // review reason) — omitted, every hop's editing surface keeps its plain default colouring.
  getHighlightColor?: (
    event: ScrapEyesEventWithReviewData
  ) => string | null | undefined;
  children: ReactNode;
}) => {
  const { t } = useTenantTranslation();
  const [request, setRequest] = useState<BringInPeriodRequest | null>(null);
  const [resolver, setResolver] = useState<
    ((result: BringInPeriodResult) => void) | null
  >(null);
  // An out-of-window neighbour's events arrive asynchronously; block confirming until they
  // do, or an early click would bring the period in with no events and the save would
  // supersede it to an empty set. Tracks the surface's current state rather than latching on
  // its first ready, because resolving the exclusions sends it back to a spinner — and the
  // capture it left behind is a merge the reviewer has just asked to change.
  const [neighbourReady, setNeighbourReady] = useState(false);
  const captureRef = useRef<CapturedEvents | null>(null);
  const titleRowId = useId();
  const stepTitleId = useId();
  const periodNoteId = useId();
  const floatingPanels = useFloatingPanelStack();

  // Unmounting mid-request (e.g. navigating away) would leave the awaiting bring-in
  // chain hung and block every later verify/submit, so settle any pending request as
  // cancelled on unmount.
  const resolverRef = useRef(resolver);
  resolverRef.current = resolver;
  useEffect(
    () => () => {
      resolverRef.current?.({ decision: "cancel" });
    },
    []
  );

  // The modal's own player runs under a nested HotkeysProvider, so suspending the
  // page's scopes leaves it unaffected.
  useDisableHotkeyScopes(
    LABELLING_HOTKEY_SCOPES,
    request !== null,
    "bringInModalSuppression"
  );

  const bringInPeriod = useCallback<BringInFn>((nextRequest) => {
    return new Promise<BringInPeriodResult>((resolve) => {
      captureRef.current = null;
      setNeighbourReady(false);
      setRequest(nextRequest);
      setResolver(() => resolve);
    });
  }, []);

  const handleNeighbourReadyChange = useCallback((isReady: boolean) => {
    setNeighbourReady(isReady);
  }, []);

  const settle = useCallback(
    (result: BringInPeriodResult) => {
      resolver?.(result);
      setResolver(null);
      setRequest(null);
    },
    [resolver]
  );

  const cancelBringIn = useCallback(() => {
    settle({ decision: "cancel" });
  }, [settle]);

  const confirmBringIn = useCallback(() => {
    if (request?.outOfWindow) {
      const captured = captureRef.current;
      if (!captured) return;
      settle({
        decision: "in",
        reviewSlotRevisions: captured.reviewSlotRevisions,
        focusEvents: captured.allEvents,
        focusOriginalEvents: [...captured.originalEventsById.values()],
        focusSeedEventsById: captured.seedEventsById,
      });
    } else {
      settle({ decision: "in" });
    }
  }, [request, settle]);

  return (
    <LinkedPeriodBringInContext value={bringInPeriod}>
      {children}
      <Dialog
        open={request !== null}
        onClose={floatingPanels.closePanelOnEscapeFirst(cancelBringIn)}
        aria-labelledby={stepTitleId}
        aria-describedby={request ? periodNoteId : undefined}
        fullWidth
        maxWidth={false}
        slotProps={{
          paper: {
            sx: (theme) => {
              const sizeWithinMargin = `calc(100% - ${theme.spacing(2 * DIALOG_MARGIN)})`;
              return {
                m: DIALOG_MARGIN,
                height: sizeWithinMargin,
                maxHeight: sizeWithinMargin,
                width: sizeWithinMargin,
              };
            },
          },
        }}
      >
        <FloatingPanelContainer stack={floatingPanels}>
          {/* DialogTitle would otherwise reuse stepTitleId, duplicating it and
              labelling the dialog with the whole row. */}
          <DialogTitle
            id={titleRowId}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              columnGap: 2,
              pb: 1,
            }}
          >
            <span id={stepTitleId}>
              {request
                ? t("linkedPeriodStep", { index: request.progress.index })
                : t("bringInPeriodTitle")}
            </span>
            {request ? (
              <Typography
                id={periodNoteId}
                component="span"
                color="text.secondary"
              >
                {t(
                  request.targetPeriod.verified_at != null
                    ? "bringInPeriodVerifiedNote"
                    : "bringInPeriodUnverifiedNote",
                  { range: formatRange(request.targetPeriod) }
                )}
              </Typography>
            ) : null}
          </DialogTitle>
          <DialogContent>
            {request ? (
              <HotkeysProvider>
                <FetchedNeighbourPlayer
                  location={location}
                  request={request}
                  captureRef={captureRef}
                  labellerExclusions={labellerExclusions}
                  omittedModelStatuses={omittedModelStatuses}
                  onReadyChange={handleNeighbourReadyChange}
                  getHighlightColor={getHighlightColor}
                />
              </HotkeysProvider>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                settle({ decision: "back" });
              }}
              disabled={!(request?.progress.canGoBack ?? false)}
            >
              {t("back")}
            </Button>
            <Stack direction="row" spacing={1}>
              <Button onClick={cancelBringIn}>{t("cancel")}</Button>
              <Button
                onClick={() => {
                  settle({ decision: "out" });
                }}
              >
                {t("bringInPeriodSkip")}
              </Button>
              <Button
                onClick={confirmBringIn}
                variant="contained"
                color="primary"
                disabled={request?.outOfWindow === true && !neighbourReady}
              >
                {t("bringInPeriodConfirm")}
              </Button>
            </Stack>
          </DialogActions>
        </FloatingPanelContainer>
      </Dialog>
    </LinkedPeriodBringInContext>
  );
};

export const useLinkedPeriodBringIn = (): BringInFn => {
  const context = use(LinkedPeriodBringInContext);
  if (!context) {
    throw new Error(
      "useLinkedPeriodBringIn must be used within a LinkedPeriodBringInProvider"
    );
  }
  return context;
};
